import {
  createOfflineOperation,
  type OperationClock,
  type OperationIdSource,
} from "../sync/operation";
import type { OfflineOperation } from "../sync/types";

export interface OperationalPolicyOperationContext {
  actorId: string;
  farmId: string;
  deviceId: string;
  performedAt: string;
}

export interface OperationalPolicyOperationOptions {
  clock?: OperationClock;
  operationIdSource?: OperationIdSource;
}

function minutesOfDay(value: string): number {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
    throw new Error("FEEDING_POLICY_ROUND_TIME_INVALID");
  }

  const parts = value.split(":");
  const hour = Number(parts[0] ?? "");
  const minute = Number(parts[1] ?? "");
  return hour * 60 + minute;
}

function validIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function buildFeedingPolicyPublishOperation(
  context: OperationalPolicyOperationContext,
  input: {
    policyName: string;
    activeWeekdays: number[];
    completionTargetMinutes: number;
    minimumGapMinutes: number;
    roundStarts: string[];
    effectiveFrom: string;
  },
  options?: OperationalPolicyOperationOptions,
): OfflineOperation {
  const policyName = input.policyName.trim();
  if (
    !context.actorId ||
    !context.farmId ||
    !context.deviceId ||
    !context.performedAt ||
    policyName.length < 1 ||
    policyName.length > 120
  ) {
    throw new Error("FEEDING_POLICY_INPUT_INVALID");
  }

  if (
    !Number.isInteger(input.completionTargetMinutes) ||
    input.completionTargetMinutes < 1 ||
    input.completionTargetMinutes > 240
  ) {
    throw new Error("FEEDING_POLICY_COMPLETION_INVALID");
  }

  if (
    !Number.isInteger(input.minimumGapMinutes) ||
    input.minimumGapMinutes < 1 ||
    input.minimumGapMinutes > 720
  ) {
    throw new Error("FEEDING_POLICY_MINIMUM_GAP_INVALID");
  }

  if (input.completionTargetMinutes > input.minimumGapMinutes) {
    throw new Error("FEEDING_POLICY_COMPLETION_EXCEEDS_GAP");
  }

  const weekdays = [...input.activeWeekdays].sort((a, b) => a - b);
  if (
    weekdays.length < 1 ||
    weekdays.length > 7 ||
    weekdays.some((value) => !Number.isInteger(value) || value < 0 || value > 6) ||
    new Set(weekdays).size !== weekdays.length
  ) {
    throw new Error("FEEDING_POLICY_WEEKDAYS_INVALID");
  }

  if (input.roundStarts.length < 1 || input.roundStarts.length > 20) {
    throw new Error("FEEDING_POLICY_ROUNDS_INVALID");
  }

  const starts = input.roundStarts.map((value) => value.trim());
  const startMinutes = starts.map(minutesOfDay);

  if (new Set(starts).size !== starts.length) {
    throw new Error("FEEDING_POLICY_ROUNDS_INVALID");
  }

  for (let index = 1; index < startMinutes.length; index += 1) {
    const previous = startMinutes[index - 1];
    const current = startMinutes[index];
    if (previous === undefined || current === undefined || current <= previous) {
      throw new Error("FEEDING_POLICY_ROUND_ORDER_INVALID");
    }
    if (current - previous < input.minimumGapMinutes) {
      throw new Error("FEEDING_POLICY_MINIMUM_GAP_VIOLATION");
    }
  }

  if (!validIsoDate(input.effectiveFrom)) {
    throw new Error("FEEDING_POLICY_EFFECTIVE_DATE_INVALID");
  }

  return createOfflineOperation(
    {
      operationType: "feeding.policy.publish",
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId: context.farmId,
      performedAt: context.performedAt,
      payload: {
        policyName,
        activeWeekdays: weekdays,
        completionTargetMinutes: input.completionTargetMinutes,
        minimumGapMinutes: input.minimumGapMinutes,
        roundStarts: starts,
        effectiveFrom: input.effectiveFrom,
      },
    },
    {
      ...(options?.clock ? { clock: options.clock } : {}),
      ...(options?.operationIdSource ? { idSource: options.operationIdSource } : {}),
    },
  );
}

export function buildFeedingDayRebuildOperation(
  context: OperationalPolicyOperationContext,
  input: { localDate: string },
  options?: OperationalPolicyOperationOptions,
): OfflineOperation {
  if (
    !context.actorId ||
    !context.farmId ||
    !context.deviceId ||
    !context.performedAt ||
    !validIsoDate(input.localDate)
  ) {
    throw new Error("FEEDING_DAY_REBUILD_INPUT_INVALID");
  }

  return createOfflineOperation(
    {
      operationType: "feeding.day_rebuild",
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId: context.farmId,
      performedAt: context.performedAt,
      payload: { localDate: input.localDate },
    },
    {
      ...(options?.clock ? { clock: options.clock } : {}),
      ...(options?.operationIdSource ? { idSource: options.operationIdSource } : {}),
    },
  );
}
